/**
 * Responsive breakpoint utilities for ArbiWin UI components
 * Provides JavaScript utilities for responsive design
 */

import React from 'react';

// Breakpoint definitions matching CSS custom properties
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

// Breakpoint names array for iteration
export const BREAKPOINT_NAMES = Object.keys(BREAKPOINTS);

/**
 * Get current breakpoint based on window width
 * @returns {string} Current breakpoint name
 */
export const getCurrentBreakpoint = function() {
  if (typeof window === 'undefined') {
    return 'lg'; // Default for SSR
  }

  const width = window.innerWidth;

  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Check if current viewport matches breakpoint
 * @param {string} breakpoint - Breakpoint name
 * @param {string} direction - 'up' (min-width) or 'down' (max-width)
 * @returns {boolean} Whether breakpoint matches
 */
export const matchesBreakpoint = function(breakpoint, direction = 'up') {
  if (typeof window === 'undefined') {
    return false;
  }

  const width = window.innerWidth;
  const breakpointValue = BREAKPOINTS[breakpoint];

  if (direction === 'up') {
    return width >= breakpointValue;
  } else {
    return width < breakpointValue;
  }
};

/**
 * Check if viewport is mobile (below md breakpoint)
 * @returns {boolean} Whether viewport is mobile
 */
export const isMobile = function() {
  return matchesBreakpoint('md', 'down');
};

/**
 * Check if viewport is tablet (md to lg)
 * @returns {boolean} Whether viewport is tablet
 */
export const isTablet = function() {
  return matchesBreakpoint('md', 'up') && matchesBreakpoint('lg', 'down');
};

/**
 * Check if viewport is desktop (lg and up)
 * @returns {boolean} Whether viewport is desktop
 */
export const isDesktop = function() {
  return matchesBreakpoint('lg', 'up');
};

/**
 * React hook for responsive breakpoint detection
 * @param {string} breakpoint - Breakpoint name to watch
 * @param {string} direction - 'up' or 'down'
 * @returns {boolean} Whether breakpoint currently matches
 */
export const useBreakpoint = function(breakpoint, direction = 'up') {
  const [matches, setMatches] = React.useState(function() {
    if (typeof window === 'undefined') {
      return false;
    }
    return matchesBreakpoint(breakpoint, direction);
  });

  React.useEffect(function() {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = function() {
      setMatches(matchesBreakpoint(breakpoint, direction));
    };

    window.addEventListener('resize', handleResize);
    return function() {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint, direction]);

  return matches;
};

/**
 * React hook for current breakpoint detection
 * @returns {string} Current breakpoint name
 */
export const useCurrentBreakpoint = function() {
  const [currentBreakpoint, setCurrentBreakpoint] = React.useState(function() {
    if (typeof window === 'undefined') {
      return 'lg';
    }
    return getCurrentBreakpoint();
  });

  React.useEffect(function() {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = function() {
      setCurrentBreakpoint(getCurrentBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return function() {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return currentBreakpoint;
};

/**
 * React hook for mobile detection
 * @returns {boolean} Whether viewport is mobile
 */
export const useIsMobile = function() {
  return useBreakpoint('md', 'down');
};

/**
 * React hook for tablet detection
 * @returns {boolean} Whether viewport is tablet
 */
export const useIsTablet = function() {
  const isMdUp = useBreakpoint('md', 'up');
  const isLgDown = useBreakpoint('lg', 'down');
  return isMdUp && isLgDown;
};

/**
 * React hook for desktop detection
 * @returns {boolean} Whether viewport is desktop
 */
export const useIsDesktop = function() {
  return useBreakpoint('lg', 'up');
};

/**
 * Get responsive value based on current breakpoint
 * @param {Object} values - Object with breakpoint keys and values
 * @param {*} defaultValue - Default value if no breakpoint matches
 * @returns {*} Value for current breakpoint
 */
export const getResponsiveValue = function(values, defaultValue) {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  const currentBp = getCurrentBreakpoint();

  // Try exact match first
  if (values[currentBp] !== undefined) {
    return values[currentBp];
  }

  // Fall back to smaller breakpoints
  const orderedBreakpoints = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs'];
  const currentIndex = orderedBreakpoints.indexOf(currentBp);

  for (let i = currentIndex; i < orderedBreakpoints.length; i++) {
    const bp = orderedBreakpoints[i];
    if (values[bp] !== undefined) {
      return values[bp];
    }
  }

  return defaultValue;
};

/**
 * React hook for responsive values
 * @param {Object} values - Object with breakpoint keys and values
 * @param {*} defaultValue - Default value
 * @returns {*} Current responsive value
 */
export const useResponsiveValue = function(values, defaultValue) {
  const currentBreakpoint = useCurrentBreakpoint();

  return React.useMemo(function() {
    return getResponsiveValue(values, defaultValue);
  }, [values, defaultValue, currentBreakpoint]);
};

/**
 * Media query utilities for CSS-in-JS
 */
export const mediaQueries = {
  xs: `@media (min-width: ${BREAKPOINTS.xs}px)`,
  sm: `@media (min-width: ${BREAKPOINTS.sm}px)`,
  md: `@media (min-width: ${BREAKPOINTS.md}px)`,
  lg: `@media (min-width: ${BREAKPOINTS.lg}px)`,
  xl: `@media (min-width: ${BREAKPOINTS.xl}px)`,
  '2xl': `@media (min-width: ${BREAKPOINTS['2xl']}px)`,

  // Max-width queries
  'xs-down': `@media (max-width: ${BREAKPOINTS.sm - 1}px)`,
  'sm-down': `@media (max-width: ${BREAKPOINTS.md - 1}px)`,
  'md-down': `@media (max-width: ${BREAKPOINTS.lg - 1}px)`,
  'lg-down': `@media (max-width: ${BREAKPOINTS.xl - 1}px)`,
  'xl-down': `@media (max-width: ${BREAKPOINTS['2xl'] - 1}px)`,

  // Range queries
  'sm-to-md': `@media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`,
  'md-to-lg': `@media (min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`,
  'lg-to-xl': `@media (min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS['2xl'] - 1}px)`
};

/**
 * Generate CSS classes for responsive utilities
 * @returns {Object} CSS classes object
 */
export const generateResponsiveClasses = function() {
  const classes = {};

  // Generate display utilities
  const displays = ['none', 'block', 'inline', 'inline-block', 'flex', 'inline-flex', 'grid', 'inline-grid'];

  displays.forEach(function(display) {
    // Base class
    classes[`arbiwin-display-${display}`] = {
      display: display
    };

    // Responsive classes
    BREAKPOINT_NAMES.forEach(function(bp) {
      if (bp !== 'xs') {
        classes[`arbiwin-${bp}-display-${display}`] = {
          [`@media (min-width: ${BREAKPOINTS[bp]}px)`]: {
            display: display
          }
        };
      }
    });
  });

  return classes;
};

/**
 * Debounced resize handler utility
 * @param {Function} callback - Callback function
 * @param {number} delay - Debounce delay in ms
 * @returns {Function} Debounced function
 */
export const debounceResize = function(callback, delay = 250) {
  let timeoutId;

  return function() {
    const args = arguments;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(function() {
      callback.apply(null, args);
    }, delay);
  };
};

/**
 * Viewport size utilities
 */
export const viewport = {
  /**
   * Get viewport width
   * @returns {number} Viewport width
   */
  getWidth: function() {
    return typeof window !== 'undefined' ? window.innerWidth : 1024;
  },

  /**
   * Get viewport height
   * @returns {number} Viewport height
   */
  getHeight: function() {
    return typeof window !== 'undefined' ? window.innerHeight : 768;
  },

  /**
   * Get viewport dimensions
   * @returns {Object} {width, height}
   */
  getDimensions: function() {
    return {
      width: this.getWidth(),
      height: this.getHeight()
    };
  }
};

/**
 * Container max-width utilities
 */
export const containerSizes = {
  sm: BREAKPOINTS.sm,
  md: BREAKPOINTS.md,
  lg: BREAKPOINTS.lg,
  xl: BREAKPOINTS.xl,
  '2xl': BREAKPOINTS['2xl']
};

/**
 * Get container max-width for breakpoint
 * @param {string} size - Container size
 * @returns {number} Max-width in pixels
 */
export const getContainerMaxWidth = function(size) {
  return containerSizes[size] || containerSizes.lg;
};

// Export default object with all utilities
const breakpointUtils = {
  BREAKPOINTS,
  BREAKPOINT_NAMES,
  getCurrentBreakpoint,
  matchesBreakpoint,
  isMobile,
  isTablet,
  isDesktop,
  getResponsiveValue,
  mediaQueries,
  generateResponsiveClasses,
  debounceResize,
  viewport,
  containerSizes,
  getContainerMaxWidth
};

export default breakpointUtils;
